import React, { useEffect } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { sendAnalyticsPageView, track } from '@leagueplatform/analytics';
import { Achievement } from '@leagueplatform/rewards-api';
import {
  Button,
  Box,
  StackLayout,
  HeadingText,
  ParagraphText,
  Modal,
} from '@leagueplatform/genesis-core';
import { BadgeImage } from '../badge-image/badge-image.view';
import { EarnedTag } from '../earned-tag/earned-tag.view';
import { ProgressBar } from '../progress-bar/progress-bar.view';

interface AchievementsDetailsModalProps {
  onModalChange: () => void;
  onCloseAutoFocus?: () => void;
  modalAchievement: Achievement;
  open: boolean;
  closeModal?: () => void;
}

export const AchievementsDetailsModal = ({
  open,
  onModalChange,
  onCloseAutoFocus,
  modalAchievement,
  closeModal,
}: AchievementsDetailsModalProps) => {
  const { formatMessage, formatDate } = useIntl();

  useEffect(() => {
    if (open) {
      sendAnalyticsPageView({
        product_area: 'rewards',
        sub_product_area: 'achievements',
        screen_name: 'achievement detail',
        sub_detail: modalAchievement.name,
        achievement_id: modalAchievement.id,
      });
    }
  }, [open, modalAchievement.name, modalAchievement.id]);

  const DATE_FORMAT = {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
  } as const; // e.g Jan 12 2022

  const isAchievementEarned = modalAchievement.completions > 0;
  const isAchievementEarnedMultiple = modalAchievement.completions > 1;
  const isAchievementInProgress = modalAchievement.completions === 0;
  const achievementStatus = isAchievementEarned ? 'earned' : 'in_progress';

  return (
    <Modal.Root open={open} onOpenChange={onModalChange}>
      <Modal.Content
        showCloseButton={false}
        css={{
          '.GDS-modal': { maxWidth: 375 },
          '.GDS-modal-content': { padding: '$twoAndHalf', paddingTop: '$none' },
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <StackLayout
          data-testid="achievement-modal"
          orientation="vertical"
          horizontalAlignment="center"
          css={{ textAlign: 'center' }}
        >
          <BadgeImage
            imageUrl={modalAchievement?.image?.large}
            isDisabled={!isAchievementEarned}
            css={{ margin: '$oneAndHalf' }}
          />
          <HeadingText level="3" size="lg">
            {modalAchievement.name}
          </HeadingText>
          {modalAchievement.last_completed_at && (
            <HeadingText
              level="4"
              size="sm"
              css={{ paddingTop: '$half', color: '$onSurfaceTextSubdued' }}
            >
              {formatMessage({ id: 'EARNED' })}{' '}
              {formatDate(modalAchievement.last_completed_at, DATE_FORMAT)}
            </HeadingText>
          )}
          <ParagraphText
            css={{
              paddingTop: '$oneAndHalf',
              color: '$onSurfaceTextSubdued',
            }}
          >
            {isAchievementInProgress
              ? modalAchievement.description
              : modalAchievement.description_earned}
          </ParagraphText>
          {isAchievementEarnedMultiple && (
            <Box css={{ paddingTop: '$threeQuarters' }}>
              <EarnedTag completionAmount={modalAchievement.completions} />
            </Box>
          )}
          {modalAchievement.display_progress && (
            <Box
              css={{
                width: '100%',
                paddingTop: '$quarter',
                textAlign: 'start',
              }}
            >
              <ProgressBar
                progressTitle={modalAchievement?.progress?.title}
                isLoading={false}
                currentActivity={modalAchievement?.progress?.criteria?.current}
                totalActivities={
                  modalAchievement?.progress?.criteria?.threshold
                }
              />
            </Box>
          )}

          <Button
            css={{
              textDecoration: 'none',
              marginTop: '$two',
            }}
            width={{
              '@initial': 'hugContents',
              '@mobile': 'fillContainer',
            }}
            onClick={() => {
              track('rewards.button_clicked.about_me_achievements.close', {
                achievement_id: modalAchievement.id,
                sub_detail: modalAchievement.name,
                achievement_status: achievementStatus,
              });
              if (closeModal) {
                closeModal();
              }
            }}
          >
            {formatMessage({ id: 'CLOSE' })}
          </Button>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
